import { SVG_NAMESPACE } from '@recogito/annotorious/src/util/SVG';

export const getFirstSelector = (data) => {
  const target = Array.isArray(data.targets) ? data.targets[0] : data.target;
  const rawSelector = target?.selector;

  return Array.isArray(rawSelector) ? rawSelector[0] : rawSelector;
};

export const parseXywh = (value) => {
  const match = /^xywh=pixel:([-\d.]+),([-\d.]+),([-\d.]+),([-\d.]+)$/.exec(
    value || '',
  );

  if (!match) return null;

  const [, x, y, w, h] = match;

  return { x: +x, y: +y, w: +w, h: +h };
};

const copyAttributes = (from, to) =>
  Array.from(from.attributes).forEach((attribute) =>
    to.setAttribute(attribute.name, attribute.value),
  );

const replaceWithEllipse = (element, bbox) => {
  const ellipse = document.createElementNS(SVG_NAMESPACE, 'ellipse');

  copyAttributes(element, ellipse);
  ['x', 'y', 'width', 'height', 'rx', 'ry'].forEach((attribute) =>
    ellipse.removeAttribute(attribute),
  );
  ellipse.setAttribute('cx', bbox.x + bbox.w / 2);
  ellipse.setAttribute('cy', bbox.y + bbox.h / 2);
  ellipse.setAttribute('rx', bbox.w / 2);
  ellipse.setAttribute('ry', bbox.h / 2);

  element.parentNode.replaceChild(ellipse, element);
};

const replaceWithPath = (element, points) => {
  const path = document.createElementNS(SVG_NAMESPACE, 'path');

  copyAttributes(element, path);
  ['x', 'y', 'width', 'height'].forEach((attribute) =>
    path.removeAttribute(attribute),
  );
  path.setAttribute(
    'd',
    points
      .map(([x, y], index) => (index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`))
      .join(' '),
  );
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');

  element.parentNode.replaceChild(path, element);
};

/**
 * Annotorious renders FragmentSelector annotations as rectangles, and its
 * formatter elements are drawn in screen space (they are counter-scaled), so
 * they cannot be used for annotation geometry. For our ellipse and pencil
 * annotations the shape the viewer draws is therefore swapped for the real
 * geometry right after Annotorious renders it.
 */
export const renderCustomShapes = (annotationLayer, annotations) => {
  if (!annotationLayer || !annotations) return;

  const byId = new Map(
    annotations.map((annotation) => [annotation.id, annotation]),
  );

  Array.from(annotationLayer.querySelectorAll('.a9s-annotation')).forEach(
    (group) => {
      const annotation = byId.get(group.getAttribute('data-id'));
      const inner = group.querySelector('.a9s-inner');

      // Editable shapes render their own geometry; only plain rects (as drawn
      // by Annotorious for FragmentSelectors) need replacing.
      if (!annotation || !inner || inner.tagName.toLowerCase() !== 'rect') {
        return;
      }

      const selector = getFirstSelector(annotation);

      if (!selector) return;

      if (selector.shape === 'ellipse') {
        const bbox = parseXywh(selector.value);

        if (bbox) {
          ['a9s-inner', 'a9s-outer'].forEach((className) => {
            const element = group.querySelector(`.${className}`);

            if (element) replaceWithEllipse(element, bbox);
          });
        }
      } else if (
        selector.shape === 'freehand' &&
        Array.isArray(selector.points)
      ) {
        ['a9s-inner', 'a9s-outer'].forEach((className) => {
          const element = group.querySelector(`.${className}`);

          if (element) replaceWithPath(element, selector.points);
        });
      }
    },
  );
};
